import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  AssetService,
  Asset,
  AssetUpdate,
  AssetDocument,
  AuditLog,
  CreateAssetData,
  UpdateAssetData,
  UpdateMaintenanceData,
  MaintenanceScheduleData,
  CreateAssetUpdateData,
  CreateAssetDocumentData,
  AxiosErrorResponse,
  PaginationParams,
  PaginationMeta,
  AssetIssue,
  UpdateAssetIssueData,
  AssetIssueFilterParams,
  CreateAssetIssueData,
} from "@/lib/api/services/assetService";
import { Site } from "@/lib/api/services/siteService";

export interface AssetState {
  assets: Asset[];
  currentAsset: Asset | null;
  assetUpdates: AssetUpdate[];
  assetDocuments: AssetDocument[];
  auditLogs: AuditLog[];
  assetIssues: AssetIssue[];
  currentIssue: AssetIssue | null;
  isLoading: boolean;
  error: string | null;
  selectedOrganizationId: number | null;
  selectedSiteId: number | null;
  pagination?: PaginationMeta;
  updatesPagination?: PaginationMeta;
  documentsPagination?: PaginationMeta;
  auditLogsPagination?: PaginationMeta;
  issuesPagination?: PaginationMeta;
  searchTerm?: string;
  currentSite: Site | null;
}

const defaultInitialState: AssetState = {
  assets: [],
  currentAsset: null,
  assetUpdates: [],
  assetDocuments: [],
  auditLogs: [],
  assetIssues: [],
  currentIssue: null,
  isLoading: false,
  error: null,
  selectedOrganizationId: null,
  selectedSiteId: null,
  pagination: undefined,
  updatesPagination: undefined,
  documentsPagination: undefined,
  auditLogsPagination: undefined,
  issuesPagination: undefined,
  searchTerm: undefined,
  currentSite: null,
};
const initialState: AssetState = defaultInitialState;

const handleApiError = (error: unknown): string => {
  const axiosError = error as AxiosErrorResponse;
  if (axiosError.response?.data) {
    const data = axiosError.response.data;
    if (Array.isArray(data.message)) {
      return data.message[0];
    }
    return data.message || "An unexpected error occurred";
  }
  return (axiosError.message as string) || "An unexpected error occurred";
};

export const createAssetIssue = createAsyncThunk(
  "asset/createAssetIssue",
  async (
    {
      organizationId,
      siteId,
      issueData,
    }: {
      organizationId: number;
      siteId: number;
      issueData: CreateAssetIssueData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.createAssetIssue(
        organizationId,
        siteId,
        issueData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetIssues = createAsyncThunk(
  "asset/getAssetIssues",
  async (
    {
      organizationId,
      siteId,
      params,
    }: {
      organizationId: number;
      siteId: number;
      params?: AssetIssueFilterParams;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetIssues(organizationId, siteId, params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetIssueById = createAsyncThunk(
  "asset/getAssetIssueById",
  async (
    {
      organizationId,
      siteId,
      issueId,
    }: {
      organizationId: number;
      siteId: number;
      issueId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetIssueById(
        organizationId,
        siteId,
        issueId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAssetIssue = createAsyncThunk(
  "asset/updateAssetIssue",
  async (
    {
      organizationId,
      siteId,
      issueId,
      issueData,
    }: {
      organizationId: number;
      siteId: number;
      issueId: number;
      issueData: UpdateAssetIssueData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateAssetIssue(
        organizationId,
        siteId,
        issueId,
        issueData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteAssetIssue = createAsyncThunk(
  "asset/deleteAssetIssue",
  async (
    {
      organizationId,
      siteId,
      issueId,
    }: {
      organizationId: number;
      siteId: number;
      issueId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.deleteAssetIssue(
        organizationId,
        siteId,
        issueId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssets = createAsyncThunk(
  "asset/getAssets",
  async (
    {
      organizationId,
      siteId,
      params,
    }: { organizationId: number; siteId: number; params?: PaginationParams },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssets(organizationId, siteId, params);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAllAuditLogs = createAsyncThunk(
  "asset/getAllAuditLogs",
  async (
    {
      organizationId,
      siteId,
      page,
      limit,
      search,
      actionType,
    }: {
      organizationId: number;
      siteId: number;
      page?: number;
      limit?: number;
      search?: string;
      actionType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAllAuditLogs(
        organizationId,
        siteId,
        page || 1,
        limit || 10,
        search,
        actionType,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAllAssetUpdates = createAsyncThunk(
  "asset/getAllAssetUpdates",
  async (
    {
      organizationId,
      siteId,
      page,
      limit,
      search,
    }: {
      organizationId: number;
      siteId: number;
      page?: number;
      limit?: number;
      search?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAllAssetUpdates(
        organizationId,
        siteId,
        page || 1,
        limit || 10,
        search,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetById = createAsyncThunk(
  "asset/getAssetById",
  async (
    {
      organizationId,
      siteId,
      assetId,
    }: { organizationId: number; siteId: number; assetId: number },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetById(organizationId, siteId, assetId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createAsset = createAsyncThunk(
  "asset/createAsset",
  async (
    {
      organizationId,
      siteId,
      assetData,
    }: { organizationId: number; siteId: number; assetData: CreateAssetData },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.createAsset(organizationId, siteId, assetData);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAsset = createAsyncThunk(
  "asset/updateAsset",
  async (
    {
      organizationId,
      siteId,
      assetId,
      assetData,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      assetData: UpdateAssetData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateAsset(
        organizationId,
        siteId,
        assetId,
        assetData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteAsset = createAsyncThunk(
  "asset/deleteAsset",
  async (
    {
      organizationId,
      siteId,
      assetId,
    }: { organizationId: number; siteId: number; assetId: number },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.deleteAsset(organizationId, siteId, assetId);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateMaintenance = createAsyncThunk(
  "asset/updateMaintenance",
  async (
    {
      organizationId,
      siteId,
      assetId,
      maintenanceData,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      maintenanceData: UpdateMaintenanceData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateMaintenance(
        organizationId,
        siteId,
        assetId,
        maintenanceData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateMaintenanceSchedule = createAsyncThunk(
  "asset/updateMaintenanceSchedule",
  async (
    {
      organizationId,
      siteId,
      assetId,
      scheduleData,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      scheduleData: MaintenanceScheduleData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateMaintenanceSchedule(
        organizationId,
        siteId,
        assetId,
        scheduleData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateHealthStatus = createAsyncThunk(
  "asset/updateHealthStatus",
  async (
    {
      organizationId,
      siteId,
      assetId,
      healthStatusId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      healthStatusId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateHealthStatus(
        organizationId,
        siteId,
        assetId,
        healthStatusId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateComplianceStatus = createAsyncThunk(
  "asset/updateComplianceStatus",
  async (
    {
      organizationId,
      siteId,
      assetId,
      complianceStatusId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      complianceStatusId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateComplianceStatus(
        organizationId,
        siteId,
        assetId,
        complianceStatusId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateCriticality = createAsyncThunk(
  "asset/updateCriticality",
  async (
    {
      organizationId,
      siteId,
      assetId,
      criticalityId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      criticalityId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateCriticality(
        organizationId,
        siteId,
        assetId,
        criticalityId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAssetState = createAsyncThunk(
  "asset/updateAssetState",
  async (
    {
      organizationId,
      siteId,
      assetId,
      assetStateId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      assetStateId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateAssetState(
        organizationId,
        siteId,
        assetId,
        assetStateId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateAssignedTo = createAsyncThunk(
  "asset/updateAssignedTo",
  async (
    {
      organizationId,
      siteId,
      assetId,
      assignedToId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      assignedToId: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateAssignedTo(
        organizationId,
        siteId,
        assetId,
        assignedToId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const updateMaintainedBy = createAsyncThunk(
  "asset/updateMaintainedBy",
  async (
    {
      organizationId,
      siteId,
      assetId,
      maintainedById,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      maintainedById: number | null;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.updateMaintainedBy(
        organizationId,
        siteId,
        assetId,
        maintainedById,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createAssetUpdate = createAsyncThunk(
  "asset/createAssetUpdate",
  async (
    {
      organizationId,
      siteId,
      assetId,
      updateData,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      updateData: CreateAssetUpdateData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.createAssetUpdate(
        organizationId,
        siteId,
        assetId,
        updateData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetUpdates = createAsyncThunk(
  "asset/getAssetUpdates",
  async (
    {
      organizationId,
      siteId,
      assetId,
      page,
      limit,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      page?: number;
      limit?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetUpdates(
        organizationId,
        siteId,
        assetId,
        page || 1,
        limit || 10,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteAssetUpdate = createAsyncThunk(
  "asset/deleteAssetUpdate",
  async (
    {
      organizationId,
      siteId,
      assetId,
      updateId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      updateId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.deleteAssetUpdate(
        organizationId,
        siteId,
        assetId,
        updateId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const createAssetDocument = createAsyncThunk(
  "asset/createAssetDocument",
  async (
    {
      organizationId,
      siteId,
      assetId,
      documentData,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      documentData: CreateAssetDocumentData;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.createAssetDocument(
        organizationId,
        siteId,
        assetId,
        documentData,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetDocuments = createAsyncThunk(
  "asset/getAssetDocuments",
  async (
    {
      organizationId,
      siteId,
      assetId,
      page,
      limit,
      search,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      page?: number;
      limit?: number;
      search?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetDocuments(
        organizationId,
        siteId,
        assetId,
        page || 1,
        limit || 10,
        search,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const deleteAssetDocument = createAsyncThunk(
  "asset/deleteAssetDocument",
  async (
    {
      organizationId,
      siteId,
      assetId,
      documentId,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      documentId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.deleteAssetDocument(
        organizationId,
        siteId,
        assetId,
        documentId,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

export const getAssetAuditLogs = createAsyncThunk(
  "asset/getAssetAuditLogs",
  async (
    {
      organizationId,
      siteId,
      assetId,
      page,
      limit,
      search,
      actionType,
    }: {
      organizationId: number;
      siteId: number;
      assetId: number;
      page?: number;
      limit?: number;
      search?: string;
      actionType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await AssetService.getAssetAuditLogs(
        organizationId,
        siteId,
        assetId,
        page || 1,
        limit || 10,
        search,
        actionType,
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    setSelectedOrganization: (state, action: PayloadAction<number>) => {
      state.selectedOrganizationId = action.payload;
    },
    setSelectedSite: (state, action: PayloadAction<number>) => {
      state.selectedSiteId = action.payload;
    },
    setCurrentAsset: (state, action: PayloadAction<Asset | null>) => {
      state.currentAsset = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAssets: () => {
      return defaultInitialState;
    },
    setCurrentSite: (state, action: PayloadAction<Site | null>) => {
      state.currentSite = action.payload;
    },
    setCurrentIssue: (state, action: PayloadAction<AssetIssue | null>) => {
      state.currentIssue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAuditLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllAuditLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.auditLogs = action.payload.logs;
        state.auditLogsPagination = action.payload.pagination;
      })
      .addCase(getAllAuditLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAllAssetUpdates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllAssetUpdates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetUpdates = action.payload.updates;
        state.updatesPagination = action.payload.pagination;
      })
      .addCase(getAllAssetUpdates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAssets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assets = action.payload.assets;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAssets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAsset = action.payload;
      })
      .addCase(getAssetById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assets.unshift(action.payload.asset);
        state.currentAsset = action.payload.asset;
      })
      .addCase(createAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteAsset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAsset.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedAssetId = action.meta.arg.assetId;
        state.assets = state.assets.filter((a) => a.id !== deletedAssetId);
        if (state.currentAsset?.id === deletedAssetId) {
          state.currentAsset = null;
        }
      })
      .addCase(deleteAsset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateMaintenance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMaintenance.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateMaintenance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateMaintenanceSchedule.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMaintenanceSchedule.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateMaintenanceSchedule.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateHealthStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateHealthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateHealthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateComplianceStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateComplianceStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateComplianceStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateCriticality.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCriticality.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateCriticality.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateAssetState.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssetState.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateAssetState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateAssignedTo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssignedTo.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateAssignedTo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateMaintainedBy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMaintainedBy.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assets.findIndex(
          (a) => a.id === action.payload.asset.id,
        );
        if (index !== -1) {
          state.assets[index] = action.payload.asset;
        }
        if (state.currentAsset?.id === action.payload.asset.id) {
          state.currentAsset = action.payload.asset;
        }
      })
      .addCase(updateMaintainedBy.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetUpdates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetUpdates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetUpdates = action.payload.updates;
        state.updatesPagination = action.payload.pagination;
      })
      .addCase(getAssetUpdates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createAssetUpdate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAssetUpdate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetUpdates.unshift(action.payload.update);
      })
      .addCase(createAssetUpdate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteAssetUpdate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAssetUpdate.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedUpdateId = action.meta.arg.updateId;
        state.assetUpdates = state.assetUpdates.filter(
          (u) => u.id !== deletedUpdateId,
        );
      })
      .addCase(deleteAssetUpdate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetDocuments = action.payload.documents;
        state.documentsPagination = action.payload.pagination;
      })
      .addCase(getAssetDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createAssetDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAssetDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetDocuments.unshift(action.payload.document);
      })
      .addCase(createAssetDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteAssetDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAssetDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedDocumentId = action.meta.arg.documentId;
        state.assetDocuments = state.assetDocuments.filter(
          (d) => d.id !== deletedDocumentId,
        );
      })
      .addCase(deleteAssetDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetAuditLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetAuditLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.auditLogs = action.payload.logs;
        state.auditLogsPagination = action.payload.pagination;
      })
      .addCase(getAssetAuditLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(createAssetIssue.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAssetIssue.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetIssues.unshift(action.payload.issue);
        state.currentIssue = action.payload.issue;
      })
      .addCase(createAssetIssue.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetIssues.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetIssues.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assetIssues = action.payload.issues;
        state.issuesPagination = action.payload.pagination;
      })
      .addCase(getAssetIssues.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getAssetIssueById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAssetIssueById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentIssue = action.payload;
      })
      .addCase(getAssetIssueById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateAssetIssue.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssetIssue.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.assetIssues.findIndex(
          (i) => i.id === action.payload.issue.id,
        );
        if (index !== -1) {
          state.assetIssues[index] = action.payload.issue;
        }
        if (state.currentIssue?.id === action.payload.issue.id) {
          state.currentIssue = action.payload.issue;
        }
      })
      .addCase(updateAssetIssue.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteAssetIssue.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAssetIssue.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedIssueId = action.meta.arg.issueId;
        state.assetIssues = state.assetIssues.filter(
          (i) => i.id !== deletedIssueId,
        );
        if (state.currentIssue?.id === deletedIssueId) {
          state.currentIssue = null;
        }
      })
      .addCase(deleteAssetIssue.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrganization,
  setSelectedSite,
  setCurrentAsset,
  setSearchTerm,
  clearError,
  resetAssets,
  setCurrentSite,
  setCurrentIssue,
} = assetSlice.actions;

export default assetSlice.reducer;
